import React, { useState } from "react";
import { Box, Typography, Grid, MenuItem, Button } from "@mui/material";
import { CalculatorTextFieldItem } from "../components/CalculatorTextFieldItem";
import CalculatorSelectFieldItem from "../components/CalculatorSelectFieldItem";

export default function BrigantiComponent() {
  const [psa, setPsa] = useState<string>("");
  const [stage, setStage] = useState<string>("cT1c");
  const [gleasonPrimary, setGleasonPrimary] = useState<string>("3");
  const [gleasonSecondary, setGleasonSecondary] = useState<string>("3");
  const [positiveCores, setPositiveCores] = useState<string>("");
  const [result, setResult] = useState<string | null>(null);

  const calculate = () => {
    const psaVal = parseFloat(psa);
    const coresVal = parseFloat(positiveCores);

    if (Number.isNaN(psaVal) || Number.isNaN(coresVal)) {
      setResult(
        "Please enter valid numeric values for PSA and % positive cores.",
      );
      return;
    }

    if (psaVal <= 0) {
      setResult("Please enter a valid Pre-op PSA greater than 0.");
      return;
    }

    if (coresVal < 0 || coresVal > 100) {
      setResult("Please enter a valid % Positive Cores between 0 and 100.");
      return;
    }

    // Briganti 2012 Nomogram Coefficients (Approximate)
    // Formula: logit = intercept + sum(beta * x)
    // Intercept: -5.334
    // PSA: 0.013
    // Stage: cT1 (0), cT2 (0.634), cT3 (1.488)
    // Gleason: <=6 (0), 7 (1.132), 8-10 (2.457)
    // % Positive Cores: 0.035

    let logit = -5.334;
    logit += 0.013 * psaVal;

    if (stage === "cT2") logit += 0.634;
    else if (stage === "cT3") logit += 1.488;

    const totalGleason =
      parseInt(gleasonPrimary, 10) + parseInt(gleasonSecondary, 10);

    if (totalGleason === 7) logit += 1.132;
    else if (totalGleason >= 8) logit += 2.457;

    logit += 0.035 * coresVal;

    const probability = (Math.exp(logit) / (1 + Math.exp(logit))) * 100;

    setResult(
      `Estimated Risk of Lymph Node Invasion: ${probability.toFixed(1)}%. ` +
        `A threshold of 5% is often used to decide on performing extended pelvic lymph node dissection (ePLND).`,
    );
  };

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Predicts the risk of lymph node invasion (LNI) in patients with
          prostate cancer.
        </Typography>

        <Grid container spacing={2}>
          <CalculatorTextFieldItem
            text="Pre-op PSA (ng/ml)"
            value={psa}
            onChange={(e) => setPsa(e.target.value)}
          />
          <CalculatorSelectFieldItem
            text="Clinical Stage"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
          >
            <MenuItem value="cT1c">cT1c</MenuItem>
            <MenuItem value="cT2">cT2 (a, b, or c)</MenuItem>
            <MenuItem value="cT3">cT3</MenuItem>
          </CalculatorSelectFieldItem>
          <CalculatorSelectFieldItem
            text="Primary Gleason Grade"
            value={gleasonPrimary}
            onChange={(e) => setGleasonPrimary(e.target.value)}
          >
            <MenuItem value="3">3</MenuItem>
            <MenuItem value="4">4</MenuItem>
            <MenuItem value="5">5</MenuItem>
          </CalculatorSelectFieldItem>
          <CalculatorSelectFieldItem
            text="Secondary Gleason Grade"
            value={gleasonSecondary}
            onChange={(e) => setGleasonSecondary(e.target.value)}
          >
            <MenuItem value="2">2</MenuItem>
            <MenuItem value="3">3</MenuItem>
            <MenuItem value="4">4</MenuItem>
            <MenuItem value="5">5</MenuItem>
          </CalculatorSelectFieldItem>
          <CalculatorTextFieldItem
            text="% Positive Cores"
            value={positiveCores}
            onChange={(e) => setPositiveCores(e.target.value)}
          />
        </Grid>

        <Button
          variant="contained"
          onClick={calculate}
          sx={{ mt: 2, mb: 2 }}
          fullWidth
        >
          Calculate Risk
        </Button>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: "#D3D3D3",
        }}
      >
        <Typography variant="h5">Result</Typography>
        {!result ? (
          <Typography>
            <i>Please fill out the fields and click Calculate</i>
          </Typography>
        ) : (
          <>
            <Typography variant="body1">{result}</Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              *Disclaimer: This is an estimation based on published coefficients
              and should be verified with official validated tools.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}
